import { useParams } from 'react-router-dom';
import GuestLayout from '../../../components/dashboard/Layouts/GuestLayout.jsx';
import logo from "../../../assets/img/logo/crafting-ai-logo.svg";
import RequireAuth from '../../../components/auth/RequireAuth';
import axios from '../../../libs/axios';
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';

import { useAuth } from '../../../hooks/auth';

const JoinTeam = () => {
    const { id } = useParams(); // id is url_key
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [joined, setJoined] = useState(false);
    const [error, setError] = useState(null);
    const { user } = useAuth();

    useEffect(() => {
        // Fetch team info by url_key
        setLoading(true);
        axios.get(`/teams/by-key/${id}`)
            .then(res => {
                setTeam(res.data);
                setLoading(false);
            })
            .catch(() => {
                setError('Could not load team info.');
                setLoading(false);
            });
    }, [id]);

    const handleJoin = async () => {
        setJoining(true);
        setError(null);
        try {
            await axios.get('/sanctum/csrf-cookie');
            await axios.post(`/teams/join/${id}`);
            setJoined(true);
            // Refetch team info to get updated members
            try {
                const res = await axios.get(`/teams/by-key/${id}`);
                setTeam(res.data);
            } catch {}
            toast.success('You have joined the team!');
        } catch (e) {
            setError('Failed to join the team.');
            toast.error('Failed to join the team.');
        } finally {
            setJoining(false);
        }
    };


    const teamName = team?.name || 'Your Team';
    // Try to show owner/admin name if available
    const admin = team?.members?.find(m => m.pivot?.role === 'owner')?.name || 'the team admin';

    // Check if user is already a member or has a pending join request
    let alreadyMember = false;
    let alreadyRequested = false;
    if (user && team && Array.isArray(team.members)) {
        const found = team.members.find(m => m.id === user.id);
        if (found) {
            if (found.pivot && found.pivot.accepted) {
                alreadyMember = true;
            } else {
                alreadyRequested = true;
            }
        }
    }

    return (
        <div className="min-h-screen w-full justify-center bg-gray-50 dark:bg-gray-900">
        <RequireAuth 
            title="Team invitation"
            description="You have been invited to join a team. Please log in to accept the invitation."
            redirectIfNotAuthenticated={`/dashboard/teams/${id}`}
            className="flex items-center justify-center w-full min-h-screen"
        >
            <GuestLayout>
                <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 transition-all duration-300">
                    <div className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 transform hover:scale-[1.02] transition-all duration-300 border border-sky-100 dark:border-sky-700">
                        {/* Error message if team does not exist or load fails */}
                        {error && (
                            <div className="flex flex-col items-center">
                                <img src={logo} alt="Crafting AI Logo" className="h-10 w-auto mx-auto opacity-80 mb-2" />
                                <h2 className="text-2xl font-extrabold text-red-700 dark:text-red-200 mb-2 drop-shadow">No team found.</h2>
                                <p className="text-base text-gray-700 dark:text-gray-300 mb-4 text-center">Please ask for the team owner to invite you with the correct link.</p>
                                <button
                                    onClick={() => window.location.href = '/dashboard/overview'}
                                        className="inline-flex items-center px-6 py-2 rounded-lg bg-gradient-to-r from-sky-500 to-sky-700 text-white font-semibold shadow hover:from-sky-600 hover:to-sky-800 transition-all text-base mt-2"
                                    >
                                        Back to Dashboard
                                </button>
                            </div>
                        )}
                        {!error && (
                        <>
                        <div className="text-center max-w-md mx-auto">
                            <img src={logo} alt="Crafting AI Logo" className="h-12 w-auto mx-auto hover:opacity-80 transition-opacity" />
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
                                Join Team
                            </h2>
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                Accept your invitation to <br /><span className="font-semibold text-sky-600 dark:text-sky-400">{teamName}</span> from <span className="font-semibold text-sky-600 dark:text-sky-400">{admin}</span><br />and start collaborating!
                            </p>
                        </div>
                        <div className="mt-8 flex flex-col gap-4 items-center">
                            {alreadyMember ? (
                                <div className="w-full py-3 px-3 rounded-lg bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 font-semibold text-center">
                                    You're already in this team
                                </div>
                            ) : alreadyRequested ? (
                                <div className="w-full py-3 rounded-lg bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 font-semibold text-center">
                                    You did request to join this team already
                                </div>
                            ) : (
                                <>
                                    <button
                                        className="w-full py-3 rounded-lg bg-gradient-to-r from-sky-400 to-sky-600 text-white font-semibold shadow hover:from-sky-500 hover:to-sky-700 transition-all text-lg disabled:opacity-60 disabled:cursor-not-allowed"
                                        onClick={handleJoin}
                                        disabled={joining || joined || loading}
                                    >
                                        {joining ? 'Joining...' : joined ? 'Joined!' : 'Join Team'}
                                    </button>
                                </>
                            )}

                            <button
                                onClick={() => window.location.href = '/dashboard/overview'}
                                className="w-full py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                            >
                                Back to Dashboard
                            </button>
                        </div>
                        </>
                        )}
                    </div>
                </div>
            </GuestLayout>
        </RequireAuth>
        </div>
    );
};

export default JoinTeam;
